import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, Loader2, ShieldAlert, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { get, post, toastError } from "@/lib/api";
import { addDaysISO, todayISO } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { noDate, RequestLine, verdictTone } from "./CapacityBits";

/**
 * «Reserver denne planen» – the only place a capacity plan becomes a write.
 * Step 1 collects the few facts the engine cannot know (soft/hard, customer, project); step 2 is a read-only
 * summary with one «Bekreft reservasjon». The API persists the block, allocates modules (hard) and audits.
 */
export const ReserveSheet = ({ plan, open, onOpenChange, onDone }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ kind: "soft", customer_id: "", project_id: "", project_name: "", expires_at: "", note: "" });
  const { data: customers } = useQuery({ queryKey: ["customers"], queryFn: () => get("/customers"), enabled: open });
  const { data: sites } = useQuery({ queryKey: ["capacity-sites"], queryFn: () => get("/capacity/sites"), enabled: open });

  const req = plan?.request || {};
  const s = plan?.summary || {};
  const dest = req.destination || {};
  const destProject = dest.project_id;

  useEffect(() => {
    if (open) setStep(1);
    if (open && plan) setF({ kind: "soft", customer_id: req.customer_id || "", project_id: destProject || "", project_name: "", expires_at: addDaysISO(todayISO(), 30), note: "" });
  }, [open, plan?.id]);

  const customer = useMemo(() => (customers || []).find((c) => c.id === f.customer_id), [customers, f.customer_id]);
  const projects = useMemo(() => (sites || []).filter((x) => x.kind === "project"), [sites]);
  const project = useMemo(() => projects.find((p) => p.id === f.project_id), [projects, f.project_id]);
  const canHard = !!s.sufficient;
  const units = Math.min(s.covered_total ?? 0, req.persons ?? 0);
  const ready = f.kind === "soft" ? true : !!f.customer_id;

  const create = useMutation({
    mutationFn: () => post("/capacity/blocks", { plan_id: plan.id || plan.plan_id, kind: f.kind, customer_id: f.customer_id || null, project_id: f.project_id || null, project_name: f.project_name || null, note: f.note || null, expires_at: f.kind === "soft" ? f.expires_at || null : null }),
    onSuccess: (b) => {
      toast.success(f.kind === "soft" ? "Soft hold registrert" : "Kapasitet kontraktsfestet", {
        description: `${b.units} senger · ${b.title} · logget i audit`,
        action: b.kind === "hard" && b.project_id ? { label: "Åpne prosjekt", onClick: () => navigate(`/prosjekter/${b.project_id}`) } : undefined,
      });
      qc.invalidateQueries({ queryKey: ["capacity-blocks"] });
      qc.invalidateQueries({ queryKey: ["capacity-outlook"] });
      qc.invalidateQueries({ queryKey: ["planning"] });
      onOpenChange(false);
      onDone?.(b);
    },
    onError: (e) => toastError(e, "Kunne ikke reservere"),
  });

  if (!plan) return null;
  const tone = verdictTone(s);
  const conflict = (plan.risks || []).find((r) => r.kind === "conflict_scenario");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-[520px] max-w-[94vw] flex-col gap-0 overflow-y-auto p-0" data-testid="capacity-reserve-sheet">
        <SheetHeader className="px-6 pt-6">
          <SheetTitle className="text-[17px]">{step === 1 ? "Reserver denne planen" : "Bekreft reservasjon"}</SheetTitle>
          <SheetDescription>
            {step === 1 ? "Ingenting er skrevet ennå. Velg hvordan kapasiteten skal holdes." : "Dette skrives til databasen og logges i audit når du bekrefter."}
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 pt-4">
          <RequestLine request={req} />
          <div className="mt-2 flex items-center gap-2 text-[12.5px]" data-testid="capacity-reserve-units">
            <span className={cn("h-2 w-2 rounded-full", tone === "success" ? "bg-emerald-500" : tone === "warning" ? "bg-amber-500" : "bg-rose-500")} aria-hidden />
            <span className="font-mono tabular font-medium">{units}</span> av {req.persons} senger · {plan.phases?.length || 0} faser · {(plan.phases || []).reduce((n, p) => n + (p.module_codes?.length || 0), 0)} moduler
          </div>
        </div>

        {step === 1 && (
          <div className="mt-5 space-y-5 px-6 pb-6">
            <div>
              <Label className="text-[12px] text-muted-foreground">Type</Label>
              <ToggleGroup type="single" value={f.kind} onValueChange={(v) => v && setF({ ...f, kind: v })} className="mt-1.5 grid grid-cols-2 gap-1 rounded-lg bg-muted p-1" data-testid="capacity-reserve-type-toggle">
                <ToggleGroupItem value="soft" className="h-auto flex-col items-start gap-0.5 rounded-md px-3 py-2 text-left data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="capacity-reserve-type-soft">
                  <span className="text-[13px] font-medium">Soft hold</span>
                  <span className="text-[11px] font-normal text-muted-foreground">Følger et tilbud. Utløper automatisk. Låser ikke moduler.</span>
                </ToggleGroupItem>
                <ToggleGroupItem value="hard" disabled={!canHard} className="h-auto flex-col items-start gap-0.5 rounded-md px-3 py-2 text-left data-[state=on]:bg-card data-[state=on]:shadow-sm disabled:opacity-50" data-testid="capacity-reserve-type-hard">
                  <span className="text-[13px] font-medium">Kontraktsfestet</span>
                  <span className="text-[11px] font-normal text-muted-foreground">{canHard ? "Låser modulene på et prosjekt." : "Krever full dekning."}</span>
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div>
              <Label className="text-[12px] text-muted-foreground">Kunde {f.kind === "hard" && "*"}</Label>
              <Select value={f.customer_id || "__none"} onValueChange={(v) => setF({ ...f, customer_id: v === "__none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="capacity-reserve-customer-input"><SelectValue placeholder="Velg kunde" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none">{f.kind === "soft" ? "Ingen (intern hold)" : "Velg kunde"}</SelectItem>
                  {(customers || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {f.kind === "hard" && (
              <div>
                <Label className="text-[12px] text-muted-foreground">Prosjekt</Label>
                <Select value={f.project_id || "__new"} onValueChange={(v) => setF({ ...f, project_id: v === "__new" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="capacity-reserve-project-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__new">+ Nytt prosjekt fra planen</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_number} · {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!f.project_id && <Input className="mt-2" placeholder={`Prosjektnavn (standard: ${customer?.name || "Kunde"} – ${dest.short || dest.name || "sted"})`} value={f.project_name} onChange={(e) => setF({ ...f, project_name: e.target.value })} data-testid="capacity-reserve-project-name-input" />}
              </div>
            )}

            {f.kind === "soft" && (
              <div>
                <Label className="text-[12px] text-muted-foreground">Utløper</Label>
                <Input type="date" className="mt-1.5" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} data-testid="capacity-reserve-expires-input" />
                <p className="mt-1 text-[11.5px] text-muted-foreground">Soft holds frigis automatisk når datoen passeres – typisk tilbudets gyldighet.</p>
              </div>
            )}

            <div>
              <Label className="text-[12px] text-muted-foreground">Notat (valgfritt)</Label>
              <Textarea className="mt-1.5 min-h-[64px]" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="F.eks. «Avventer signering, kunde ønsker oppstart 1. des.»" data-testid="capacity-reserve-note-input" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-5 px-6 pb-6">
            <dl className="grid grid-cols-[minmax(90px,auto)_1fr] gap-x-4 gap-y-2 text-[13px] leading-5" data-testid="capacity-reserve-summary">
              <dt className="text-muted-foreground">Type</dt><dd>{f.kind === "soft" ? "Soft hold (utløper automatisk)" : "Kontraktsfestet reservasjon"}</dd>
              <dt className="text-muted-foreground">Kunde</dt><dd>{customer?.name || (f.kind === "soft" ? "Intern hold" : "–")}</dd>
              <dt className="text-muted-foreground">Sted</dt><dd>{dest.name || "–"}</dd>
              <dt className="text-muted-foreground">Periode</dt><dd className="font-mono tabular">{noDate(req.start_date)} – {noDate(req.end_date)}</dd>
              <dt className="text-muted-foreground">Senger</dt><dd className="font-mono tabular">{units} av {req.persons}</dd>
              {f.kind === "hard" && <><dt className="text-muted-foreground">Prosjekt</dt><dd>{project ? `${project.project_number} · ${project.name}` : `${f.project_name || `${customer?.name || "Kunde"} – ${dest.short || dest.name || ""}`} (nytt)`}</dd></>}
              {f.kind === "soft" && <><dt className="text-muted-foreground">Utløper</dt><dd className="font-mono tabular">{noDate(f.expires_at)}</dd></>}
              {f.note && <><dt className="text-muted-foreground">Notat</dt><dd>{f.note}</dd></>}
            </dl>
            <div className="mt-4 divide-y divide-border/70 rounded-lg bg-muted/40 px-3 text-[12.5px]" data-testid="capacity-reserve-phases">
              {(plan.phases || []).map((ph) => (
                <div key={ph.index} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0 truncate">{ph.origin_label} · {ph.source_label}</span>
                  <span className="shrink-0 font-mono tabular text-muted-foreground">{noDate(ph.ready_at)}</span>
                  <span className="shrink-0 font-mono tabular font-medium">+{ph.units}</span>
                </div>
              ))}
            </div>
            {f.kind === "hard" && (
              <p className="mt-4 flex items-start gap-2 text-[12px] leading-5 text-amber-800 dark:text-amber-300">
                <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} /> Modulene låses som bekreftede allokeringer på prosjektet fra hver fases klar-dato. Kan frigis igjen fra blokklisten.
              </p>
            )}
            {f.kind === "soft" && conflict && (
              <p className="mt-4 flex items-start gap-2 text-[12px] leading-5 text-amber-800 dark:text-amber-300">
                <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} /> {conflict.title}. Soft holds kan overlappe – konflikten vises i utsikten.
              </p>
            )}
          </div>
        )}

        <SheetFooter className="mt-auto flex-row items-center gap-2 border-t border-border/70 px-6 py-4 sm:justify-between">
          {step === 2 ? (
            <Button variant="ghost" size="sm" onClick={() => setStep(1)} data-testid="capacity-reserve-back-button"><ChevronLeft className="h-4 w-4" /> Tilbake</Button>
          ) : (
            <span className="text-[11.5px] text-muted-foreground">Ingenting skrives før du bekrefter.</span>
          )}
          {step === 1 ? (
            <Button onClick={() => setStep(2)} disabled={!ready} data-testid="capacity-reserve-confirm-button">Gå til bekreftelse <ArrowUpRight className="h-4 w-4" /></Button>
          ) : (
            <Button onClick={() => create.mutate()} disabled={create.isPending} data-testid="capacity-reserve-final-confirm-button">
              {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Bekreft reservasjon
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
